import type { InventoryItemData } from '@jobilla/nexus-sdk'
import type { TFunction } from 'i18next'

/** Selected quantity per inventory item id. */
export type InventorySelection = Record<string, number>

export type InventoryAllocationLine = {
	item: InventoryItemData
	quantity: number
	valueCents: number
}

export type InventoryAllocation = {
	lines: InventoryAllocationLine[]
	appliedCents: number
	coveredCents: number
	remainingCents: number
	topUpCents: number
}

/**
 * Mirrors the backend's campaign invoice: selected inventory counts towards the
 * campaign cost (budget plus add-ons), and any value above it becomes a Wallet Topup.
 */
export function allocateInventory(
	items: InventoryItemData[],
	selection: InventorySelection,
	campaignCostCents: number,
): InventoryAllocation {
	const lines = items
		.filter((item) => (selection[item.id] ?? 0) > 0)
		.map((item) => {
			const quantity = selection[item.id]!
			return { item, quantity, valueCents: item.valueCents * quantity }
		})

	const appliedCents = lines.reduce((sum, line) => sum + line.valueCents, 0)

	return {
		lines,
		appliedCents,
		coveredCents: Math.min(appliedCents, campaignCostCents),
		remainingCents: Math.max(0, campaignCostCents - appliedCents),
		topUpCents: Math.max(0, appliedCents - campaignCostCents),
	}
}

export function isSelectableInventoryItem(item: InventoryItemData): boolean {
	// Invoiced items are tracked and can be converted to wallet credit, but campaign-launch
	// funding support for them (invoice discounting, surplus handling) ships separately —
	// keep them out of this picker until that lands, so launching a campaign only ever
	// consumes uninvoiced items via the existing, unchanged invoicing-on-expiry logic.
	return !item.isExpired && item.availableQuantity > 0 && !item.isInvoiced
}

export function inventoryItemName(item: InventoryItemData, t: TFunction<'editor'>): string {
	return item.description
		?? t(`billingDrawer.inventoryTypes.${item.catalogueItemType}`, { defaultValue: item.catalogueItemType })
}
